/**
 * TrendHistoryTab — closed Resident Trends episodes for one resident.
 *
 * Lives on its own tab (mirroring Logs) so a growing history does not crowd
 * the Basic Info tab. The tab itself is only rendered by the page when the
 * resident has at least one closed episode.
 *
 * Columns: Closed, Opened, Status, Outcome, Action Taken, Concern (narrative)
 */

import { useState, useMemo, useCallback } from 'react';
import usePersistedPerPage from '../../../../../hooks/usePersistedPerPage.js';
import {
  STATUS_CONFIG, OUTCOME_CONFIG, ACTION_OPTIONS,
} from '../../../../../components/resident-trends/trendConfig.js';

const actionLabel = (value) => {
  if (!value) return '—';
  const opt = ACTION_OPTIONS.find((o) => o.value === value);
  return opt ? opt.label : value;
};

const formatDate = (value) => (value ? String(value).slice(0, 10) : '—');

const TrendHistoryTab = ({ episodes }) => {
  const [search, setSearch] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [sortCol, setSortCol] = useState('closed_date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();

  const rows = episodes || [];

  // Distinct outcomes present, for the filter dropdown
  const outcomes = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.outcome) set.add(r.outcome); });
    return [...set];
  }, [rows]);

  const filtered = useMemo(() => {
    let out = [...rows];

    if (search) {
      const q = search.toLowerCase();
      out = out.filter((r) =>
        (r.narrative || '').toLowerCase().includes(q)
        || actionLabel(r.action_taken).toLowerCase().includes(q)
        || (r.outcome || '').toLowerCase().includes(q)
        || (r.status || '').toLowerCase().includes(q)
        || (r.signal_families || '').toLowerCase().includes(q)
      );
    }

    if (outcomeFilter) out = out.filter((r) => r.outcome === outcomeFilter);

    out.sort((a, b) => {
      const aVal = (a[sortCol] ?? '').toString().toLowerCase();
      const bVal = (b[sortCol] ?? '').toString().toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return out;
  }, [rows, search, outcomeFilter, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);

  const handleSort = useCallback((col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
    setPage(1);
  }, [sortCol]);

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <span className="dp-sort-icon dp-sort-icon--neutral dp-icon-xs">↕</span>;
    return <span className="dp-sort-icon dp-sort-icon--active dp-icon-xs">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="pp-section">
      <div className="pp-section__header">
        <h3 className="pp-section__title">Trend History</h3>
        <div className="pp-section__actions">
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
            {filtered.length} {filtered.length === 1 ? 'episode' : 'episodes'}
          </span>
        </div>
      </div>
      <div className="pp-section__body">
        {/* Controls */}
        <div className="dp-controls">
          <div className="dp-controls__left">
            <div className="dp-search">
              <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                className="dp-search__input"
                type="text"
                placeholder="Search trend history…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            {outcomes.length > 1 ? (
              <select
                className="dp-select"
                value={outcomeFilter}
                onChange={(e) => { setOutcomeFilter(e.target.value); setPage(1); }}
                style={{ maxWidth: '200px' }}
              >
                <option value="">All Outcomes</option>
                {outcomes.map((o) => (
                  <option key={o} value={o}>{OUTCOME_CONFIG[o]?.label || o}</option>
                ))}
              </select>
            ) : null}
          </div>
        </div>

        {/* Table */}
        {rows.length === 0 ? (
          <div className="dp-empty" style={{ marginTop: 'var(--space-4)' }}>
            <p className="dp-empty__title">No trend history</p>
            <p className="dp-empty__text">Closed trend concerns for this resident will appear here.</p>
          </div>
        ) : (
          <>
            <div className="dp-table-wrap" style={{ marginTop: 'var(--space-3)' }}>
              <table className="dp-table">
                <thead className="dp-thead">
                  <tr>
                    <th className="dp-th">
                      <div className="dp-th__content">
                        <button className="dp-th__sort-btn" onClick={() => handleSort('closed_date')}>
                          Closed <SortIcon col="closed_date" />
                        </button>
                      </div>
                    </th>
                    <th className="dp-th">
                      <div className="dp-th__content">
                        <button className="dp-th__sort-btn" onClick={() => handleSort('opened_date')}>
                          Opened <SortIcon col="opened_date" />
                        </button>
                      </div>
                    </th>
                    <th className="dp-th">
                      <div className="dp-th__content">
                        <button className="dp-th__sort-btn" onClick={() => handleSort('status')}>
                          Status <SortIcon col="status" />
                        </button>
                      </div>
                    </th>
                    <th className="dp-th">
                      <div className="dp-th__content">
                        <button className="dp-th__sort-btn" onClick={() => handleSort('outcome')}>
                          Outcome <SortIcon col="outcome" />
                        </button>
                      </div>
                    </th>
                    <th className="dp-th">
                      <div className="dp-th__content">Action Taken</div>
                    </th>
                    <th className="dp-th">
                      <div className="dp-th__content">Concern</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row) => {
                    const status = STATUS_CONFIG[row.status] || {};
                    const outcome = OUTCOME_CONFIG[row.outcome] || null;
                    return (
                      <tr key={row.episode_id} className="dp-tr">
                        <td className="dp-td">
                          <span className="dp-cell-text dp-cell-text--muted" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>
                            {formatDate(row.closed_date)}
                          </span>
                        </td>
                        <td className="dp-td">
                          <span className="dp-cell-text dp-cell-text--muted" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>
                            {formatDate(row.opened_date)}
                          </span>
                        </td>
                        <td className="dp-td">
                          <span style={{ fontSize: 10, fontWeight: 600, color: status.color || 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            {status.label || row.status}
                          </span>
                        </td>
                        <td className="dp-td">
                          {outcome ? (
                            <span style={{ fontSize: 10, fontWeight: 600, color: outcome.color, background: `${outcome.color}14`, padding: '1px 8px', borderRadius: 'var(--radius-full)', whiteSpace: 'nowrap' }}>
                              {outcome.label}
                            </span>
                          ) : (
                            <span className="dp-cell-text dp-cell-text--muted">—</span>
                          )}
                        </td>
                        <td className="dp-td">
                          <span className="dp-cell-text">{actionLabel(row.action_taken)}</span>
                        </td>
                        <td className="dp-td">
                          <span
                            className="dp-cell-text"
                            title={row.narrative || ''}
                            style={{ display: 'block', maxWidth: 460, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {row.narrative || '—'}
                          </span>
                          {row.occurrence_number > 1 ? (
                            <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                              Occurrence #{row.occurrence_number}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filtered.length > perPage ? (
              <div className="dp-pagination">
                <span className="dp-pagination__info">
                  Showing <span className="dp-pagination__emphasis">{(page - 1) * perPage + 1}</span>
                  –<span className="dp-pagination__emphasis">{Math.min(page * perPage, filtered.length)}</span>
                  {' '}of <span className="dp-pagination__emphasis">{filtered.length}</span>
                </span>
                <div className="dp-pagination__controls">
                  <div className="dp-per-page">
                    <span className="dp-per-page__label">Per page:</span>
                    <select className="dp-per-page__select" value={perPage} onChange={(e) => { setPerPage(+e.target.value); setPage(1); }}>
                      {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="dp-page-nav">
                    <button className="dp-page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
                      Math.max(0, page - 3), Math.min(totalPages, page + 2)
                    ).map((p) => (
                      <button
                        key={p}
                        className={`dp-page-num${p === page ? ' is-active' : ''}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button className="dp-page-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>›</button>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default TrendHistoryTab;
